import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { churchesService } from '@/services/churches.service';
import type { ChurchListing, ChurchMultiSelectOption } from '@/types/churches';

const DEFAULT_LIMIT = 10;
const DEFAULT_DEBOUNCE_MS = 400;
const SCROLL_LOAD_THRESHOLD_PX = 40;

type UsePaginatedChurchOptionsParams = {
  searchTerm: string;
  enabled?: boolean;
  limit?: number;
  debounceMs?: number;
};

const mapChurchToOption = (church: ChurchListing): ChurchMultiSelectOption => ({
  value: church.id,
  label: church.zipCode ? `${church.name} (${church.zipCode})` : church.name,
});

export const usePaginatedChurchOptions = ({
  searchTerm,
  enabled = true,
  limit = DEFAULT_LIMIT,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UsePaginatedChurchOptionsParams) => {
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, debounceMs);
  const [churchOptions, setChurchOptions] = useState<ChurchMultiSelectOption[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [total, setTotal] = useState<number>(0);
  const [isSearchingChurches, setIsSearchingChurches] = useState<boolean>(false);
  const [isLoadingMoreChurches, setIsLoadingMoreChurches] = useState<boolean>(false);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);

  const hasMore = churchOptions.length < total;

  useEffect(() => {
    if (!enabled) {
      setChurchOptions([]);
      setCursor(undefined);
      setTotal(0);
      return;
    }

    const searchChurches = async () => {
      setIsSearchingChurches(true);
      try {
        const response = await churchesService.getChurches({
          term: debouncedSearchTerm || undefined,
          limit,
        });
        setChurchOptions(response.items.map(mapChurchToOption));
        setTotal(response.total);
        if (response.items.length > 0) {
          setCursor(response.items[response.items.length - 1].id);
          return;
        }
        setCursor(undefined);
      } finally {
        setIsSearchingChurches(false);
      }
    };

    searchChurches();
  }, [debouncedSearchTerm, enabled, limit]);

  const loadMoreChurches = useCallback(async () => {
    if (!enabled || !hasMore || isLoadingMoreChurches || isSearchingChurches || !cursor) {
      return;
    }

    setIsLoadingMoreChurches(true);
    try {
      const response = await churchesService.getChurches({
        term: debouncedSearchTerm || undefined,
        limit,
        cursor,
      });
      setChurchOptions((prev) => {
        const existingIds = new Set(prev.map((option) => option.value));
        const nextOptions = response.items
          .map(mapChurchToOption)
          .filter((option) => !existingIds.has(option.value));
        return [...prev, ...nextOptions];
      });
      setTotal(response.total);
      if (response.items.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } finally {
      setIsLoadingMoreChurches(false);
    }
  }, [cursor, debouncedSearchTerm, enabled, hasMore, isLoadingMoreChurches, isSearchingChurches, limit]);

  const handleScrollPositionChange = useCallback(
    ({ y }: { x: number; y: number }) => {
      const viewport = scrollViewportRef.current;
      if (!viewport) {
        return;
      }

      const isNearBottom =
        y + viewport.clientHeight >= viewport.scrollHeight - SCROLL_LOAD_THRESHOLD_PX;
      if (isNearBottom) {
        loadMoreChurches();
      }
    },
    [loadMoreChurches]
  );

  const scrollAreaProps = useMemo(
    () => ({
      viewportRef: scrollViewportRef,
      onScrollPositionChange: handleScrollPositionChange,
    }),
    [handleScrollPositionChange]
  );

  return {
    churchOptions,
    isSearchingChurches,
    isLoadingMoreChurches,
    scrollAreaProps,
  };
};
